import { createAdminProspectRequestGate } from '../../services/admin/prospectTargetingFilters.js';
import { expectedSourceFromProvenance } from './adminProspectDisplay.js';

export const ADMIN_PROSPECT_PROJECTION_SECTIONS = Object.freeze([
  'primaryContact',
  'latestOwnerProvidedPropertyProfile',
  'latestOwnerProvidedFinancialSnapshot',
]);

export const ADMIN_PROSPECT_PROJECTION_VERIFICATION_STATES = Object.freeze([
  'unverified',
  'admin_verified',
]);

const SECTION_SET = new Set(ADMIN_PROSPECT_PROJECTION_SECTIONS);

/**
 * Latest-wins tracker that also records the prospect that owned a verification call.
 * Invalidating on route change prevents prospect A results from applying to B.
 */
export function createAdminProspectVerificationTracker() {
  let latestRequestId = 0;
  return {
    begin(prospectId) {
      const requestId = ++latestRequestId;
      return {
        prospectId: typeof prospectId === 'string' ? prospectId.trim() : '',
        requestId,
      };
    },
    invalidate() {
      latestRequestId += 1;
      return latestRequestId;
    },
    isCurrent(owner) {
      return Boolean(owner && owner.requestId === latestRequestId);
    },
    get latestRequestId() {
      return latestRequestId;
    },
  };
}

export function createClearedAdminProspectVerificationUi() {
  return {
    busy: null,
    pending: null,
    error: null,
    refreshWarning: false,
  };
}

export function canonicalAdminProspectVerificationState(value) {
  return value === 'admin_verified' ? 'admin_verified' : 'unverified';
}

/**
 * Freeze the compare-and-set payload when the administrator first requests a change.
 * Confirmation must reuse this object and must not recompute from later card renders.
 */
export function freezeAdminProspectVerificationAction({
  prospectId,
  section,
  currentVerificationState,
  provenance,
} = {}) {
  const id = typeof prospectId === 'string' ? prospectId.trim() : '';
  if (!id || !SECTION_SET.has(section)) return null;
  const expectedSource = expectedSourceFromProvenance(provenance);
  if (!expectedSource) return null;
  const expectedVerificationState = canonicalAdminProspectVerificationState(currentVerificationState);
  return {
    prospectId: id,
    section,
    verificationState: expectedVerificationState === 'admin_verified' ? 'unverified' : 'admin_verified',
    expectedVerificationState,
    expectedSource,
  };
}

/**
 * Whether a verification mutation/refresh result may update Prospect 360 UI.
 * Requires mount, request ownership, and prospect ownership.
 */
export function shouldApplyAdminProspectVerificationResult({
  mounted,
  owner,
  tracker,
  activeProspectId,
} = {}) {
  if (!mounted || !owner || !tracker?.isCurrent(owner)) return false;
  if (!owner.prospectId || !activeProspectId) return false;
  return owner.prospectId === activeProspectId;
}

export function shouldStartAdminProspectOwnedLoad({ ownerProspectId, activeProspectId } = {}) {
  return Boolean(ownerProspectId && activeProspectId && ownerProspectId === activeProspectId);
}

export function createAdminProspectVerificationSession({ prospectId, detail = null } = {}) {
  return {
    mounted: true,
    prospectId,
    detail,
    ui: createClearedAdminProspectVerificationUi(),
    verifyTracker: createAdminProspectVerificationTracker(),
    detailGate: createAdminProspectRequestGate(),
    mutationsSent: [],
    refreshesSent: [],
    inflightVerification: null,
    inflightRefresh: null,
  };
}

export function unmountAdminProspectVerificationSession(session) {
  session.mounted = false;
}

export function applyAdminProspectRouteChange(session, nextProspectId) {
  session.verifyTracker.invalidate();
  session.prospectId = nextProspectId;
  session.ui = createClearedAdminProspectVerificationUi();
  const refreshOwner = {
    prospectId: nextProspectId,
    requestId: session.detailGate.next(),
    reason: 'route',
  };
  session.inflightRefresh = refreshOwner;
  session.refreshesSent.push({ prospectId: nextProspectId, reason: 'route' });
  return refreshOwner;
}

export function beginAdminProspectVerification(session, pending) {
  if (!pending || session.ui.busy) return null;
  const owner = session.verifyTracker.begin(session.prospectId);
  session.inflightVerification = owner;
  session.ui.busy = pending.section;
  session.ui.error = null;
  session.mutationsSent.push({
    prospectId: pending.prospectId,
    section: pending.section,
    verificationState: pending.verificationState,
    expectedVerificationState: pending.expectedVerificationState,
  });
  return owner;
}

export function applyAdminProspectVerificationMutationResult(session, owner, { ok, error } = {}) {
  if (
    !shouldApplyAdminProspectVerificationResult({
      mounted: session.mounted,
      owner,
      tracker: session.verifyTracker,
      activeProspectId: session.prospectId,
    })
  ) {
    return { applied: false };
  }
  if (!ok) {
    session.ui.error = error || { kind: 'mutation' };
    session.ui.busy = null;
    session.inflightVerification = null;
    return { applied: true, kind: 'mutation-error' };
  }
  session.ui.pending = null;
  session.ui.error = null;
  const refreshOwner = {
    prospectId: owner.prospectId,
    requestId: session.detailGate.next(),
    reason: 'post-mutation',
    verifyOwner: owner,
  };
  session.inflightRefresh = refreshOwner;
  session.refreshesSent.push({ prospectId: owner.prospectId, reason: 'post-mutation' });
  return { applied: true, kind: 'mutation-success', refreshOwner };
}

export function applyAdminProspectDetailResult(session, owner, { ok, detail } = {}) {
  const owns =
    session.mounted &&
    owner &&
    session.detailGate.isCurrent(owner.requestId) &&
    owner.prospectId === session.prospectId;
  if (!owns) return { applied: false };
  if (ok) {
    session.detail = detail;
    session.ui.refreshWarning = false;
    session.ui.error = null;
    if (owner.reason === 'post-mutation') {
      session.ui.busy = null;
      session.inflightVerification = null;
    }
    return { applied: true, kind: 'refresh-success' };
  }
  if (owner.reason === 'post-mutation' || owner.reason === 'reload') {
    session.ui.refreshWarning = true;
    session.ui.busy = null;
    session.inflightVerification = null;
    return { applied: true, kind: 'refresh-error' };
  }
  return { applied: true, kind: 'detail-error' };
}

export function reloadAdminProspectAfterRefreshFailure(session) {
  const refreshOwner = {
    prospectId: session.prospectId,
    requestId: session.detailGate.next(),
    reason: 'reload',
  };
  session.inflightRefresh = refreshOwner;
  session.refreshesSent.push({ prospectId: session.prospectId, reason: 'reload' });
  return refreshOwner;
}
